import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, useTheme, useMediaQuery } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PropTypes from 'prop-types';

// Anchos de tarjeta por breakpoint
const CARD_WIDTHS = {
  xs: 330,
  sm: 390,
  md: 420,
  lg: 440,
};
const GAP = 32;

function getCardWidth(theme) {
  if (useMediaQuery(theme.breakpoints.up('lg'))) return CARD_WIDTHS.lg;
  if (useMediaQuery(theme.breakpoints.up('md'))) return CARD_WIDTHS.md;
  if (useMediaQuery(theme.breakpoints.up('sm'))) return CARD_WIDTHS.sm;
  return CARD_WIDTHS.xs;
}

function Carousel({ children }) {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  // Responsive visibleCount
  let responsiveVisibleCount = 1;
  if (isLg) {
    responsiveVisibleCount = 5;
  } else if (isMd) {
    responsiveVisibleCount = 3;
  } else if (isSm) {
    responsiveVisibleCount = 2;
  }

  const total = React.Children.count(children);
  const [activeIndex, setActiveIndex] = useState(responsiveVisibleCount);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const trackRef = useRef(null);

  // Responsive card width
  const cardWidth = getCardWidth(theme);

  // Duplicar slides para efecto infinito
  const slides = [
    ...React.Children.toArray(children).slice(-responsiveVisibleCount),
    ...React.Children.toArray(children),
    ...React.Children.toArray(children).slice(0, responsiveVisibleCount),
  ];

  // Calcula el desplazamiento para centrar la tarjeta activa
  const getTranslateX = () => {
    if (total === 0) return 0;
    const half = Math.floor(responsiveVisibleCount / 2);
    // Calcula el índice real considerando los duplicados
    let realIndex = activeIndex;
    if (activeIndex < responsiveVisibleCount) {
      realIndex = total + activeIndex;
    } else if (activeIndex >= total + responsiveVisibleCount) {
      realIndex = activeIndex - total;
    }
    return -((realIndex - half) * (cardWidth + GAP));
  };

  // Navegación infinita
  const goToPrev = () => {
    if (isTransitioning || total === 0) return;
    setIsTransitioning(true);
    setActiveIndex((prev) => prev - 1);
  };
  const goToNext = () => {
    if (isTransitioning || total === 0) return;
    setIsTransitioning(true);
    setActiveIndex((prev) => prev + 1);
  };
  const goToIndex = (idx) => {
    if (total === 0) return;
    setActiveIndex(idx + responsiveVisibleCount); // Ajusta por los duplicados al inicio
  };

  // Efecto para el salto seamless
  useEffect(() => {
    if (!isTransitioning || total === 0) return;

    const handle = setTimeout(() => {
      setIsTransitioning(false);

      // Si estamos en los slides duplicados del inicio
      if (activeIndex < responsiveVisibleCount) {
        setActiveIndex(total + activeIndex);
      }
      // Si estamos en los slides duplicados del final
      else if (activeIndex >= total + responsiveVisibleCount) {
        setActiveIndex(activeIndex - total);
      }
    }, 500);

    return () => clearTimeout(handle);
  }, [activeIndex, isTransitioning, total, responsiveVisibleCount]);

  // Responsive width for the carousel container
  const containerWidth = {
    xs: `${
      responsiveVisibleCount * CARD_WIDTHS.xs +
      (responsiveVisibleCount - 1) * GAP
    }px`,
    sm: `${
      responsiveVisibleCount * CARD_WIDTHS.sm +
      (responsiveVisibleCount - 1) * GAP
    }px`,
    md: `${
      responsiveVisibleCount * CARD_WIDTHS.md +
      (responsiveVisibleCount - 1) * GAP
    }px`,
    lg: `${
      responsiveVisibleCount * CARD_WIDTHS.lg +
      (responsiveVisibleCount - 1) * GAP
    }px`,
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" width="100%">
      {/* Carrusel con scroll infinito */}
      <Box
        sx={{
          width: containerWidth,
          overflow: 'hidden',
          position: 'relative',
          paddingY: 5,
        }}
      >
        <Box
          ref={trackRef}
          display="flex"
          gap={`${GAP}px`}
          sx={{
            transition: isTransitioning
              ? 'transform 0.5s cubic-bezier(0.4,0,0.2,1)'
              : 'none',
            transform: `translateX(${getTranslateX()}px)`,
          }}
        >
          {slides.map((child, idx) => (
            <Box
              key={idx}
              sx={{
                width: {
                  xs: CARD_WIDTHS.xs,
                  sm: CARD_WIDTHS.sm,
                  md: CARD_WIDTHS.md,
                  lg: CARD_WIDTHS.lg,
                },
                flexShrink: 0,
              }}
            >
              {child}
            </Box>
          ))}
        </Box>
      </Box>
      {/* Dots */}
      <Box display={'flex'} alignItems={'center'} gap={2}>
        <IconButton
          sx={{ 
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark'
            }
          }}
          onClick={goToPrev}
          aria-label="Anterior"
        >
          <ArrowBackIosNewIcon sx={{ color: 'white' }} />
        </IconButton>
        <Box display="flex" alignItems={'center'} gap={1}>
          {Array.from({ length: total }).map((_, idx) => (
            <Box
              key={idx}
              onClick={() => goToIndex(idx)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor:
                  (activeIndex - responsiveVisibleCount) % total === idx
                    ? 'primary.main'
                    : 'grey.400',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
            />
          ))}
        </Box>
        <IconButton
          sx={{ 
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark'
            }
          }}
          onClick={goToNext}
          aria-label="Siguiente"
        >
          <ArrowForwardIosIcon sx={{ color: 'white' }} />
        </IconButton>
      </Box>
    </Box>
  );
}

Carousel.propTypes = {
  children: PropTypes.node,
};

export default Carousel;
